/* eslint-disable camelcase, no-undef, new-cap */
import React, { Component, Suspense } from 'react';
import { connect } from 'react-redux';
import isEqual from 'lodash.isequal';
import { helpers } from '@kattana-io/common';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import DexDatafeed from './datafeeds/DexDatafeed';
import CexDatafeed from './datafeeds/CexDatafeed';
import { StorageKeys } from '../../helpers/localStorage';
import {
  convertSymbol,
  pairConvertable,
  convertTestnetSymbol,
} from './datafeeds/helpers/symbols';
import {
  widget,
  ChartingLibraryFeatureset,
  IBasicDataFeed,
  Timezone,
  CustomTimezoneId,
  CustomAliasedTimezone,
  EntityInfo,
} from './charting_library';
import { SaveLoadAdapter } from './saveLoadAdapter';
import { enableTradesOverlay } from './tvParts/enableTradesOverlay';
import { enableLimitsOverlay } from './tvParts/enableLimitsOverlay';
import { enableOthersLimitsOverlay } from './tvParts/otherLimitsOverlay';
import { actionShowModal } from '../../actions/modal';
import { actionSetTimezone } from '../../actions/timezone';
import { actionSetDrawMarks, actionSetRemoveMarks } from '../../actions/dex_trades';
import { actionCreateNotification } from '../../actions/notifications';
import { roundToPrecision, pricePrecision, trimZeros } from '../../helpers/truncate';
import { onTxScan } from '../../helpers/addressUtils';
import LiquidityChart from './LiquidityGraph';
import { filterTimeframes } from '../../helpers/pricingPlans';
import {
  binanceSupportedResolutions,
  gateioSupportedResolutions,
  hitbtcSupportedResolutions,
  huobiSupportedResolutions,
  kucoinSupportedResolutions,
  okxSupportedResolutions,
  bybitSupportedResolutions,
  dexSupportedResolutions,
} from './datafeeds/helpers/timeframes';
import ChartHeader from './ChartHeader';

import './styles.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

const LAST_RESOLUTION_KEY = 'tradingview.chart.lastUsedTimeBasedResolution'; // from TV

enum BtnColor {
  Disabled = 'rgba(67, 70, 76, 0.5)',
  USD = '#d1d4dc',
  Quote = 'pink',
}

interface ILiquidityChartState {
  chain: string;
  address: string;
  mounted: boolean;
  hidden: boolean;
}

class TradingViewChart extends Component<TradingViewChartProps, ILiquidityChartState> {
  widget: any;
  divRef: any;
  orderLines: any;
  withChartReadyTimer: any;
  chartData: any;
  chartSettingsLoaded: boolean | undefined;
  id: number | undefined;
  showMarks = false;
  removeLimitsShapes: Function | undefined;
  removeTradesShapes: Function | undefined;
  conversionState: { pair?: string, flipped: boolean, convertBtn?: HTMLButtonElement };
  volumeStudy: string | null;

  constructor(props: TradingViewChartProps) {
    super(props);
    this.divRef = React.createRef();
    this.orderLines = [];
    this.chartData = null;
    this.conversionState = { flipped: false };
    this.volumeStudy = null;

    this.state = {
      chain: props.chain,
      address: props.address,
      hidden: true,
      mounted: false,
    };
    try {
      const persistedChartData = localStorage.getItem(StorageKeys.tv_chart);
      if (persistedChartData) {
        const cachedData = JSON.parse(persistedChartData);
        this.chartData = cachedData;
      }
    } catch (err) {
      this.chartData = null;
    }
  }

  componentDidMount() {
    const {
      exchange,
      pair,
      flipped,
      chain,
      base_address,
      quote_address,
      timeframeExcludes,
      uniswapLiquidity,
      tokenUnlocks,
    } = this.props;
    const canConvert = pairConvertable(chain, flipped ? base_address : quote_address);
    this.mountChart(exchange, pair, flipped, chain, canConvert, timeframeExcludes, uniswapLiquidity, tokenUnlocks);
  }

  /**
   * In some cases we need to change symbol
   * in other cases - resize
   * if exchange changed - remount
   */
  componentDidUpdate(prevProps: TradingViewChartProps) {
    const { orders, height, width, id } = prevProps;

    const pairChanged = !isEqual(id, this.props.id);
    // const exchangeChanged = !isEqual(exchange, this.props.exchange);
    const heightChanged = !isEqual(height, this.props.height);
    const widthChanged = !isEqual(width, this.props.width);
    const ordersChanged = !isEqual(orders, this.props.orders);

    if (heightChanged || widthChanged) {
      if (this.divRef) {this.resizeIFrame(width, height);}
    }

    /**
     * Pair could be switched event when chart is not ready
     */
    if (pairChanged) {
      this.withChartReady((chart: any) => {
        const { pair, address, kind, chain } = this.props;
        const isDex = kind == 2;
        const symbol = pair.split('-').reverse().join('');

        chart.setSymbol(isDex ? convertTestnetSymbol(chain, address) : symbol);
      });
    }
    if (ordersChanged) {
      this.withChartReady((chart: any) => this.mountOrders(chart, orders));
    }
  }

  /**
   * Handle unmount
   */
  componentWillUnmount() {
    if (this.widget) {
      this.widget.remove();
    }
  }

  /**
   * Return callback if ready or wait 3 seconds
   */
  withChartReady = (onReady: any) => {
    // eslint-disable-next-line no-underscore-dangle
    if (this.widget && this.widget._ready) {
      onReady(this.widget.chart());
    } else {
      this.withChartReadyTimer = setTimeout(() => this.withChartReady(onReady), 3000);
    }
  }

  /**
   * Remove all orders in this.orderLines
   * Fill current with data from redux (props)
   */
  mountOrders = (chart: any, orders: any) => {
    this.orderLines.forEach((o: any) => o.remove());
    this.orderLines = orders.map((order: any) => {
      const { price, side, amount } = order;
      const Text = [
        'sell', 'ask', 1
      ].includes(side) ? 'Sell' : 'Buy';
      return chart.createOrderLine({ Editable: true })
        .setPrice(price)
        .setQuantity(amount)
        .setText(Text)
        .onCancel(() => this.props.onCancelOrder(order));
    });
  }

  adjustWidth = (width: number) => (width - 15);

  getTimezoneData = () => {
    try {
      const userTimezone = dayjs.tz.guess();
      const now = new Date();
      const timezoneOffsetInMinutes = now.getTimezoneOffset();
      const timezoneOffsetInHours = timezoneOffsetInMinutes / 60;

      const hours = Math.floor(Math.abs(timezoneOffsetInHours));
      const minutes = Math.abs(timezoneOffsetInMinutes % 60);
      const sign = timezoneOffsetInHours < 0 ? '-' : '+';
      const splittedTimezone = userTimezone.split('/');

      const customTimezones = [{
        id: userTimezone as CustomTimezoneId,
        title: splittedTimezone.length > 1
          ? splittedTimezone[1].replace('_', ' ')
          : userTimezone,
        alias:
          minutes > 0
            ? `Etc/GMT${sign}${hours}:${minutes}`
            : `Etc/GMT${sign}${hours}`,
      }] as CustomAliasedTimezone[];

      return {
        timezone: userTimezone as Timezone,
        custom_timezones: customTimezones,
      };
    } catch (error) {
      console.log(error);
      return {};
    }
  };

  formatterFn = (price: number) =>
    trimZeros(roundToPrecision(price, true, pricePrecision(price)));

  resizeIFrame = (width: number, height: number) => {
    const { current } = this.divRef;
    const { children } = current;
    const iframe = children[0];
    iframe.height = height;
    iframe.width = this.adjustWidth(width);
  }

  /**
   * We should remove widget to avoid glitches
   */
  dismountChart = () => {
    if (this.widget) {
      try {
        this.widget.remove();
      } catch (e) {
        console.error(e);
      }
    }
  }

  removeAllShapes = () => {
    if (this.removeLimitsShapes) {
      this.removeLimitsShapes();
    }
    if (this.removeTradesShapes) {
      this.removeTradesShapes();
    }
  }

  convertButtonClick = () => {
    const chart = this.widget.chart();
    const { pair, flipped, convertBtn } = this.conversionState;

    this.showMarks = false;
    this.removeAllShapes();
    if (this.volumeStudy) {
      chart.removeEntity(this.volumeStudy);
    }

    if (convertBtn && pair) {
      try {
        const quote = convertSymbol(pair, flipped);
        if (this.widget._options.datafeed.usdConversion) {
          this.widget._options.datafeed.usdConversion = false;
          this.updateBtnStyles(convertBtn, BtnColor.USD, 'USD', quote);
        } else {
          this.widget._options.datafeed.usdConversion = true;
          this.updateBtnStyles(convertBtn, BtnColor.Quote, quote, 'USD');
        }
        this.widget._options.datafeed.onResetCacheNeededCallback();
        chart.resetData();
        const sym = chart.symbol();
        chart.setSymbol(
          this.widget._options.datafeed.usdConversion ? ' ' + sym : sym + ' '
        );
        chart
          .createStudy('Volume', true, false)
          .then((study: string | null) => this.volumeStudy = study);
      } catch (e) {
        console.error(e);
      }
    }
  };

  liquidityButtonClick = (btn: HTMLButtonElement) => {
    const { hidden } = this.state;
    if (hidden) {
      this.setState({ hidden: false });
      this.updateBtnStyles(
        btn,
        BtnColor.Quote,
        'Hide Liquidity',
        'Hide liquidity Analytics',
      );
    } else {
      this.setState({ hidden: true });
      this.updateBtnStyles(
        btn,
        BtnColor.USD,
        'Liquidity',
        'Show liquidity Analytics',
      );
    }
  }

  updateBtnStyles = (
    btn: HTMLButtonElement,
    color: BtnColor,
    text?: string,
    title?: string,
    pointer?: string
  ) => {
    btn.style.color = color;
    if (pointer) {
      btn.style.pointerEvents = pointer;
      if (pointer === 'none') {
        btn.style.cursor = 'not-allowed';
      } else {
        btn.style.cursor = 'pointer';
      }
    }
    if (text) {
      btn.textContent = text;
    }
    if (title) {
      btn.setAttribute('title', title);
    }
  }

  mountChart = (
    exchange: string,
    pair: string,
    flipped: boolean,
    chain: string,
    canConvert: boolean,
    timeframeExcludes: string[],
    uniswapLiquidity: boolean,
    tokenUnlocks: boolean,
  ) => {
    let buttonUSD: HTMLButtonElement;
    let buttonLiquidity: HTMLButtonElement;
    const datafeed = getDatafeed(exchange, chain, canConvert, timeframeExcludes, tokenUnlocks);
    const { width, height, orders, id, address, canChangePair, compact, kind } = this.props;

    let disabled_features: ChartingLibraryFeatureset[] = ['header_symbol_search'];

    const enabled_features: ChartingLibraryFeatureset[] = [
      'use_localstorage_for_settings',
      'move_logo_to_main_pane',
      'seconds_resolution',
      'hide_left_toolbar_by_default',
      'two_character_bar_marks_labels',
    ];

    // if (!canChangePair) {
    //   disabled_features.push('header_symbol_search');
    // }

    /**
     * Enter mobile mode when we use phone
     * notice: https://github.com/tradingview/charting_library/issues/1174
     */
    if (compact) {
      disabled_features = [
        ...disabled_features,
        // 'header_widget',
        // 'control_bar',
        'left_toolbar',
        'timeframes_toolbar',
        'edit_buttons_in_legend',
        'context_menus',
        'border_around_the_chart',
      ];
      // enabled_features.push('narrow_chart_enabled');
    }

    if (!exchange || !datafeed) {return;}

    const interval = localStorage.getItem(LAST_RESOLUTION_KEY) || '60';
    // interval may not be supported by current datafeed, like 1s on binance or 3h on hitbtc
    const datafeedSupportsInterval = (datafeed as any).isIntervalSupported(interval);

    let libraryPath = 'https://lib-cdn.kattana.io/v25/charting_library/';
    if (process.env.REACT_LOCAL_DEV) {
      libraryPath = '/tv-chart/build/charting_library/';
    }
    const timezoneData = this.getTimezoneData();

    const isDex = kind == 2;
    this.widget = new widget({
      // debug: true, // uncomment this line to see Library errors and warnings in the console
      fullscreen: false,
      symbol: isDex ? convertTestnetSymbol(chain, address) : pair.split('-').reverse().join(''),
      interval: (datafeedSupportsInterval ? interval : '60') as any,
      toolbar_bg: '#090C15',
      loading_screen: { backgroundColor: '#090C15' },
      header_widget_buttons_mode: 'compact',
      // allow_symbol_change: false,
      container: `tradingviewchart_${id}`,
      datafeed,
      library_path: libraryPath, // IMPORTANT
      locale: 'en',

      disabled_features,
      enabled_features,
      // charts_storage_url: 'https://analytics.kattana.trade/charts',
      // snapshot_url: 'https://analytics.kattana.trade/charts/snapshot',
      charts_storage_api_version: '1.1',
      client_id: 'kattana-app',
      user_id: '', // settings.get('token'),
      theme: 'dark',
      height,
      width: this.adjustWidth(width),
      save_load_adapter: new SaveLoadAdapter(),
      load_last_chart: true,
      ...timezoneData,
      custom_formatters: {
        priceFormatterFactory: () => ({
          format: this.formatterFn
        })
      } as any,
    });

    this.widget.onChartReady(() => {
      try {
        const chart = this.widget.chart();
        const timezoneApi = chart.getTimezoneApi();
        this.chartSettingsLoaded = true;
        this.removeAllShapes();
        this.volumeStudy = chart.getAllStudies().find(
          (study: EntityInfo) => study.name === 'Volume'
        )?.id || null;
        this.widget.subscribe('onAutoSaveNeeded', () => {
          this.widget.saveChartToServer(() => {
            console.log('chart saved successfully');
          },
          () => {
            console.error('error on chart saving');
          },
          {
            chartName: `${StorageKeys.tv_chart}-${this.props.exchange}-${this.props.pair}`
          });
        });

        this.widget.subscribe('onMarkClick', (id: string | number) => {
          const txHash = id.toString().split('-')?.[0];
          onTxScan(txHash, this.props.chain);
        });

        this.props.actionSetDrawMarks?.(() => {
          chart.refreshMarks();
        });
        this.props.actionSetRemoveMarks?.(() => {
          chart.clearMarks(1);
        });
        this.props.actionSetTimezone?.(timezoneApi.getTimezone().id);
        timezoneApi.onTimezoneChanged().subscribe(
          null,
          (timezone?: string) => this.props.actionSetTimezone?.(timezone),
          false
        );

        chart.onSymbolChanged().subscribe(null, (sym: any) => {
          const ticker = sym.data.ticker;
          const isAddressChanged = this.id !== ticker.id;

          if (this.id && isAddressChanged && buttonUSD) {
            this.id = ticker.id;

            this.conversionState = {
              pair: ticker.name,
              flipped: ticker.flipped,
              convertBtn: buttonUSD
            };
            this.setState({ chain: ticker.chain, address: ticker.address });

            const quote = convertSymbol(ticker.name, ticker.flipped);
            const canConvert = pairConvertable(ticker.chain, sym.data.quote_address);
            const isUniswap = ticker.exchange === helpers.exchanges.UNISWAP;
            this.updateBtnStyles(
              buttonUSD,
              canConvert ? BtnColor.Quote : BtnColor.Disabled,
              quote,
              'USD',
              canConvert ? 'auto' : 'none'
            );
            if (uniswapLiquidity) {
              if (isUniswap) {
                this.updateBtnStyles(
                  buttonLiquidity,
                  BtnColor.USD,
                  this.state.hidden ? 'Liquidity' : 'Hide Liquidity',
                  this.state.hidden ? 'Show liquidity Analytics' : 'Hide liquidity Analytics',
                  'auto'
                );
              } else {
                this.setState({ hidden: true });
                this.updateBtnStyles(
                  buttonLiquidity,
                  BtnColor.Disabled,
                  'Liquidity',
                  'Show liquidity Analytics',
                  'none'
                );
              }
            }

            this.widget._options.datafeed.usdConversion = canConvert;
          }
        });
        this.mountOrders(chart, orders);

        this.setState({ mounted: true });
      } catch (err) {
        console.warn('TradingView chart catched error');
      }
    });

    this.widget.headerReady().then(() => {
      const isDex = kind == 2;
      /**
       * Show marks button
       */
      if (isDex && this.props.actionShowModal && this.props.actionCreateNotification) {
        // temporary disable
        // this.removeAllShapes();
        // this.removeTradesShapes = enableTradesOverlay(this.widget, this.props.exchange, address, this.props.actionShowModal);
        this.removeLimitsShapes = enableLimitsOverlay(this.widget, this.props.chain, address, this.props.actionCreateNotification, this.props.actionShowModal);
        // NOTICE: Seems like backend is not working properly
        // this.props.exchange === helpers.exchanges.UNISWAP ? this.removeLimitsShapes =
        //   enableOthersLimitsOverlay(this.widget, this.props.base_address, this.props.quote_address, this.props.base_decimals, this.props.quote_decimals)
        //   : null;
      }

      if (isDex) {
        this.id = this.props.id;

        /**
         * Liquidity chart button
         */
        if (uniswapLiquidity) {
          buttonLiquidity = this.widget.createButton();
          const isUniswap = this.props.exchange === helpers.exchanges.UNISWAP;
          this.updateBtnStyles(
            buttonLiquidity,
            isUniswap ? BtnColor.USD : BtnColor.Disabled,
            'Liquidity',
            'Show liquidity Analytics',
            isUniswap ? 'auto' : 'none'
          );
          buttonLiquidity.addEventListener('click', () => this.liquidityButtonClick(buttonLiquidity));
        }

        /**
         * USD converation button
         */
        buttonUSD = this.widget.createButton();

        this.conversionState = {
          pair: this.props.pair,
          flipped: this.props.flipped,
          convertBtn: buttonUSD
        };

        const convertableAddress = this.props.flipped ? this.props.base_address: this.props.quote_address;
        const canConvert = pairConvertable(this.props.chain, convertableAddress);
        this.updateBtnStyles(
          buttonUSD,
          canConvert ? BtnColor.Quote : BtnColor.Disabled,
          convertSymbol(this.props.pair, this.props.flipped),
          'USD',
          canConvert ? 'auto' : 'none'
        );

        buttonUSD.addEventListener('click', this.convertButtonClick);
      }
    });
  }

  render() {
    const { id, compact } = this.props;
    const { chain, address, hidden } = this.state;
    const chartId = `tradingviewchart_${id}`;
    return (
      <div className='chart_widget'>
        <div className='chart_widget__container'>
          {!compact && <ChartHeader />}
          <div
            className="iframeLimitator"
            ref={this.divRef}
            id={chartId}
          />
        </div>
        {!hidden && (
          <Suspense fallback={null}>
            <LiquidityChart chain={chain} address={address} />
          </Suspense>
        )}
      </div>
    );
  }
}
export interface TradingViewChartProps {
  embed: boolean
  intervals: any
  width: number
  height: number
  chain: string
  kind: number;
  exchange: string
  pair: string
  address: string
  base_address: string
  quote_address: string
  base_decimals: number
  quote_decimals: number
  orders: any[];
  timeframeExcludes: string[];
  uniswapLiquidity: boolean;
  tokenUnlocks: boolean;
  rawOrders: any[]
  id: number
  markets: any
  compact: boolean
  canChangePair: boolean
  onCancelOrder: Function;
  actionShowModal?: Function;
  actionSetTimezone?: Function;
  actionSetDrawMarks?: Function;
  actionSetRemoveMarks?: Function;
  actionCreateNotification?: Function;
  flipped: boolean;
}

export interface ICEXPriceUpdateEvent {
  price: string;
  base_volume: string;
  tickerId: number;
}

/**
 * Return UDF datafeed instance
 * @param exchange
 */
export const getDatafeed = (
  exchange: string,
  chain: string,
  canConvert: boolean,
  excludes: string[],
  tokenUnlocks?: boolean,
): IBasicDataFeed | undefined => {
  switch (exchange) {
  case helpers.exchanges.BINANCE:
    return new CexDatafeed({
      debug: true,
      exchange: helpers.exchanges.BINANCE,
      supportedResolution: filterTimeframes(binanceSupportedResolutions, excludes),
    });
  case helpers.exchanges.HITBTC:
    return new CexDatafeed({
      debug: true,
      exchange: helpers.exchanges.HITBTC,
      supportedResolution: filterTimeframes(hitbtcSupportedResolutions, excludes),
    });
  case helpers.exchanges.GATEIO:
    return new CexDatafeed({
      debug: true,
      exchange: helpers.exchanges.GATEIO,
      supportedResolution: filterTimeframes(gateioSupportedResolutions, excludes),
    });
  case helpers.exchanges.HUOBI:
    return new CexDatafeed({
      debug: true,
      exchange: helpers.exchanges.HUOBI,
      supportedResolution: filterTimeframes(huobiSupportedResolutions, excludes),
    });
  case helpers.exchanges.KUCOIN:
    return new CexDatafeed({
      debug: true,
      exchange: helpers.exchanges.KUCOIN,
      supportedResolution: filterTimeframes(kucoinSupportedResolutions, excludes),
    });
  case helpers.exchanges.OKX:
    return new CexDatafeed({
      debug: true,
      exchange: helpers.exchanges.OKX,
      supportedResolution: filterTimeframes(okxSupportedResolutions, excludes),
    });
  case helpers.exchanges.BYBIT:
    return new CexDatafeed({
      debug: true,
      exchange: helpers.exchanges.BYBIT,
      supportedResolution: filterTimeframes(bybitSupportedResolutions, excludes),
    });
  default:
    if (exchange == 'ORPHAN' || chain != '') {
      return new DexDatafeed({
        debug: false,
        usdConversion: canConvert,
        supportedResolution: filterTimeframes(dexSupportedResolutions, excludes),
        tokenUnlocks,
      });
    }
    return undefined;
  }
};

export default connect(null, {
  actionShowModal,
  actionSetTimezone,
  actionSetDrawMarks,
  actionCreateNotification,
  actionSetRemoveMarks,
})(TradingViewChart);
